import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

interface RollbackRow {
  readonly rollbackId: string;
  readonly rollbackCode: string;
  readonly deploymentId: string | null;
  readonly rollbackType: string;
  readonly status: string;
  readonly reason: string | null;
  readonly completedAt: string | null;
}

const TYPES = ['', 'VERSION', 'CONFIGURATION', 'RELEASE', 'EMERGENCY', 'SELECTIVE', 'FULL'];
const STATUSES = ['', 'INITIATED', 'IN_PROGRESS', 'COMPLETED', 'FAILED'];
const CREATE_TYPES = TYPES.filter(Boolean);

/**
 * M32 — Rollback Management Screen (§31 / §20). Register of rollback executions
 * (T430) against deployments with rollback type, reason and lifecycle status.
 * Supports initiating a rollback and driving it through approve → start →
 * complete / fail, each wired to the guarded /v1/devops/rollbacks write API.
 * Filterable by rollback type and status; the completed/failed counts feed the
 * rollback-rate DORA metric and BR-07 emergency-rollback governance checks.
 */
export function RollbackManagementPage(): ReactNode {
  const { t: tr } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { token } = useAuth();
  const [rows, setRows] = useState<RollbackRow[]>([]);
  const [rollbackType, setRollbackType] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [newType, setNewType] = useState('VERSION');
  const [newDeploymentId, setNewDeploymentId] = useState('');
  const [newReason, setNewReason] = useState('');
  const [failDetail, setFailDetail] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (rollbackType) params.set('rollbackType', rollbackType);
    if (status) params.set('status', status);
    const qs = params.toString();
    apiRequest<{ data: RollbackRow[] }>(`/v1/devops/rollbacks${qs ? `?${qs}` : ''}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, rollbackType, status, reload]);

  const failMessage = (err: unknown): string =>
    err instanceof ApiError && err.status === 403
      ? tr('m32.rollback.authError')
      : (err as Error).message;

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!newDeploymentId.trim()) { setActionError(tr('m32.rollback.deploymentIdRequired')); return; }
    setBusy(true);
    try {
      await apiRequest(`/v1/devops/rollbacks`, {
        method: 'POST',
        body: { rollbackType: newType, deploymentId: newDeploymentId.trim(), reason: newReason.trim() || undefined },
        token,
      });
      setNotice(tr('m32.rollback.initiated'));
      setNewDeploymentId('');
      setNewReason('');
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (r: RollbackRow, path: string, label: string, body: Record<string, unknown> = {}): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest(`/v1/devops/rollbacks/${r.rollbackId}/${path}`, { method: 'POST', body, token });
      setNotice(tr('m32.rollback.actNotice', { code: r.rollbackCode, label }));
      setReload((n) => n + 1);
    } catch (err) {
      setActionError(failMessage(err));
    }
  };

  return (
    <main aria-label="rollback-management">
      <h1>{tr('m32.rollback.title')}</h1>

      <section aria-label="initiate-rollback">
        <h2>{tr('m32.rollback.initiateTitle')}</h2>
        <form aria-label="rollback-form" onSubmit={(e) => void create(e)}>
          <label>{tr('m32.rollback.type')}
            <select aria-label="field-rollback-type" value={newType} onChange={(e) => setNewType(e.target.value)}>
              {CREATE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>{tr('m32.rollback.fieldDeploymentId')}<input aria-label="field-deployment-id" value={newDeploymentId} onChange={(e) => setNewDeploymentId(e.target.value)} /></label>
          <label>{tr('m32.rollback.fieldReason')}<input aria-label="field-reason" value={newReason} onChange={(e) => setNewReason(e.target.value)} /></label>
          <button type="submit" aria-label="submit-rollback" disabled={busy}>{busy ? tr('m32.rollback.initiating') : tr('m32.rollback.initiateBtn')}</button>
        </form>
        {actionError && <p role="alert" aria-label="rollback-error">{actionError}</p>}
        {notice && <p aria-label="rollback-notice">{notice}</p>}
      </section>

      <div className="filters">
        <label>
          {tr('m32.rollback.type')}
          <select aria-label="filter-rollback-type" value={rollbackType} onChange={(e) => setRollbackType(e.target.value)}>
            {TYPES.map((t) => <option key={t || 'all'} value={t}>{t || tr('m32.rollback.all')}</option>)}
          </select>
        </label>
        <label>
          {tr('m32.rollback.status')}
          <select aria-label="filter-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((s) => <option key={s || 'all'} value={s}>{s || tr('m32.rollback.all')}</option>)}
          </select>
        </label>
      </div>
      {error && <p role="alert">{error}</p>}
      {loading ? <p>{tr('m32.rollback.loading')}</p> : (
        <>
          <p aria-label="rollback-count">{tr('m32.rollback.count', { count: rows.length })}</p>
          <table aria-label="rollback-table">
            <thead><tr><th>{tr('m32.rollback.thCode')}</th><th>{tr('m32.rollback.thDeployment')}</th><th>{tr('m32.rollback.type')}</th><th>{tr('m32.rollback.thReason')}</th><th>{tr('m32.rollback.thExecutedAt')}</th><th>{tr('m32.rollback.status')}</th><th>{tr('m32.rollback.thActions')}</th></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.rollbackId} data-status={r.status}>
                  <td>{r.rollbackCode}</td><td>{r.deploymentId ?? '—'}</td><td>{r.rollbackType}</td>
                  <td>{r.reason ?? '—'}</td><td>{at(r.completedAt)}</td><td>{r.status}</td>
                  <td>
                    {r.status === 'INITIATED' && (
                      <>
                        <button type="button" aria-label={`approve-${r.rollbackCode}`} onClick={() => void act(r, 'approve', tr('m32.rollback.act.approved'))}>{tr('m32.rollback.approve')}</button>
                        <button type="button" aria-label={`start-${r.rollbackCode}`} onClick={() => void act(r, 'start', tr('m32.rollback.act.started'))}>{tr('m32.rollback.start')}</button>
                      </>
                    )}
                    {r.status === 'IN_PROGRESS' && (
                      <>
                        <button type="button" aria-label={`complete-${r.rollbackCode}`} onClick={() => void act(r, 'complete', tr('m32.rollback.act.completed'))}>{tr('m32.rollback.complete')}</button>
                        <input aria-label={`fail-input-${r.rollbackCode}`} placeholder={tr('m32.rollback.failurePlaceholder')} value={failDetail[r.rollbackId] ?? ''} onChange={(e) => setFailDetail((m) => ({ ...m, [r.rollbackId]: e.target.value }))} />
                        <button type="button" aria-label={`fail-${r.rollbackCode}`} onClick={() => void act(r, 'fail', tr('m32.rollback.act.failed'), { detail: (failDetail[r.rollbackId] ?? '').trim() || undefined })}>{tr('m32.rollback.fail')}</button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </main>
  );
}
